// tslint:disable: deprecation
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Observable } from 'rxjs';
import { IHeaderMenuItem } from '../header/IHeaderState';
import { IOrganization } from '../models/organization/IOrganization';
import { IPractice } from '../models/practice/IPractice';
import { IAuthenticatedUser } from '../models/user-interfaces';
import { BackgroundClass, BackgroundClasses, BackgroundClassList, ILayoutOptions, ILayoutVisibilitySettings } from './ILayoutOptions';
import { FoundationLayoutService } from './layout.service';

/**
 * The master layout component which is used within the app component to wrap the header, footer, and main sections for the entire app.
 *
 * Usage: Adding content with the classes `header-content`, `body-content`, and `footer-content` will project them in
 * to the appropriate part of the template. Additionally, Foundation-Header inputs/properties can be set on this component.
 */
@Component({
    // tslint:disable-next-line:component-selector
    selector: 'ofi-foundation-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    standalone: false
})
export class FoundationLayoutComponent implements OnInit, OnChanges {
    /** The name of the classes that will be applied to the background. No value will  */
    @Input() backgroundClassList?: Array<string | BackgroundClassList | BackgroundClass | BackgroundClasses>;
    /** The logged in visitor's information. */
    @Input() user: IAuthenticatedUser;
    /** The name override that will appear next to the user icon. When not provided, the name will come from the user object. **The user's first name is recommended**. */
    @Input() username: string;
    /** Which items should appear in the header. */
    @Input() visibility: ILayoutVisibilitySettings;
    /** Experimental Details about the selected organization. Pre-populates the organization dropdown, overriding the selection made by checking the user org. */
    @Input() organization?: IOrganization;
    /** Experimental: The list of organizations to present in the organization select. */
    @Input() organizations?: Array<IOrganization> = [];
    /** Details about the current practice, which are used to brand the header and in practice selection. */
    @Input() practice?: IPractice;
    /** A list of practices to present in the practice select. */
    @Input() practices?: Array<IPractice> = [];
    /** The items that should appear in the menu when the user clicks their name. */
    @Input() userMenuItems?: Array<IHeaderMenuItem>;

    /** @deprecated A callback to be used when pressing the menu button in the header. */
    @Input() menuButtonCB: Function;

    /** Emits the full practice object on selection. */
    @Output() practiceSelectionChanged = new EventEmitter<IPractice>();
    /** Emits the full practice object on selection. */
    @Output() organizationSelectionChanged = new EventEmitter<IOrganization>();

    /** ignore */
    data$: Observable<ILayoutOptions>;

    /** @ignore */
    constructor (private _layoutService: FoundationLayoutService) {
        this.data$ = this._layoutService.state$;
    }

    /** @ignore */
    ngOnInit (): void {
        this.backgroundClassList = this.backgroundClassList
            ? [ 'layout', ...this.backgroundClassList ]
            : [ 'layout', 'responsive', 'bg-fill-white', 'bg-tools', 'background-tools' ];
    }

    /** @ignore */
    ngOnChanges (changes: SimpleChanges): void {
        for (const propName in changes) {
            if (changes.hasOwnProperty(propName) && propName !== 'menuButtonCB' && this._layoutService[propName]) {
                this._layoutService[propName] = changes[propName].currentValue;
                if (propName === 'backgroundClassList' && changes[propName].firstChange === false) {
                    this.backgroundClassList = [ 'layout', ...changes.backgroundClassList.currentValue ];
                }
            }
        }
    }

    /** Save and emit the selected practice value. */
    onSelectPractice = (practice) => {
        this.practiceSelectionChanged.emit(practice);
        this._layoutService.practiceValue = practice;
    };

    /** Save and emit the selected organization value. */
    onSelectOrganization (organization) {
        this.organizationSelectionChanged.emit(organization);
        this._layoutService.organizationValue = organization;
    }
}
